import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Col, Row } from 'reactstrap'
import moment from 'moment'
import ComplexTable from '../../components/Table/ComplexTable'
import { IUserState } from '../../store/user/types'
import Section from '../../components/Section'
import SearchInput from '../../components/Inputs/SearchInput'
import { IServicesState } from '../../store/services/types'
import {
    getAccountServices,
    getCustomerServiceGroups,
    getServiceGroups,
    resetServicesStore,
    setServicesSearchString,
    toggleServicesLoading,
} from '../../store/services/actions'
import SimpleLoader from '../../components/SimpleLoader'
import Refresh from '../../components/SvgIcons/Refresh'
import { checkPermission } from '../../utils/Auth/AuthService'
import { setSystemForbiddenError } from '../../store/system/actions'
import {
    ACCOUNT_TYPE_RESALE_PARTNER,
    IAccountState,
} from '../../store/account/types'
import { AppDispatch } from '../../configureStore'
import MetaButton from '../../components/MetaButton/MetaButton'
import SearchIcon from '../../components/SvgIcons/SearchIcon'
import ServiceSubmenu from '../../components/Services/ServiceSubmenu'
import {
    buildFallbackServiceNavigationSections,
    normalizeService,
    resolveServiceGroupName,
    serviceMatchesSection,
} from '../../helpers/services/normalize'
import {
    TNormalizedService,
    TServiceNavigationSection,
} from '../../types/serviceView'

type TOwnProps = {
    user: IUserState
    services: IServicesState
    dispatch: AppDispatch
    account: IAccountState
}

const Services: FunctionComponent<TOwnProps> = ({
    dispatch,
    user,
    account,
    services: servicesState,
}) => {
    const {
        services,
        loading,
        searchString,
        serviceGroups,
        customerServiceGroups,
    } = servicesState
    const location = useLocation()
    const navigate = useNavigate()
    const activeSectionId = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        return searchParams.get('group') || 'all'
    }, [location.search])
    const navigationSections: TServiceNavigationSection[] =
        buildFallbackServiceNavigationSections(
            customerServiceGroups.length
                ? customerServiceGroups
                : serviceGroups,
            services
        )

    useEffect(() => {
        if (!checkPermission('services.read', user.permissions)) {
            dispatch(setSystemForbiddenError())
        } else if (
            services.length === 0 ||
            account.accountTypeCode === ACCOUNT_TYPE_RESALE_PARTNER
        ) {
            refreshStore()
        }
    }, [])

    const getData = async () => {
        dispatch(toggleServicesLoading())

        try {
            const groupsLoaded = await dispatch(getServiceGroups())
            if (!groupsLoaded) {
                return
            }

            await dispatch(getAccountServices())
            dispatch(getCustomerServiceGroups())
        } finally {
            dispatch(toggleServicesLoading())
        }
    }

    const refreshStore = () => {
        dispatch(resetServicesStore())
        void getData()
    }

    const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setServicesSearchString(event.target.value.toString()))
    }

    const onClearSearch = () => {
        dispatch(setServicesSearchString(''))
    }

    const getRouteSection = (): TServiceNavigationSection | null => {
        return (
            navigationSections.find(
                (section) => section.id === activeSectionId
            ) || null
        )
    }

    const getGroupName = (group?: number | string): string => {
        return resolveServiceGroupName(group, serviceGroups) || ''
    }

    const getNormalizedServices = (): TNormalizedService[] =>
        services.map((service) => {
            const groupName = getGroupName(service.group)
            return normalizeService(service, {
                groupName,
            })
        })

    const matchesSearch = (service: TNormalizedService): boolean => {
        const query = searchString.trim().toLowerCase()
        if (!query) {
            return true
        }

        const searchable = [
            service.name,
            service.productCode,
            service.type,
            service.orderNumber || '',
            service.groupName || '',
        ]
            .join(' ')
            .toLowerCase()

        return searchable.includes(query)
    }

    const getVisibleServices = (): TNormalizedService[] => {
        const routeSection = getRouteSection()

        return getNormalizedServices().filter(
            (service) =>
                serviceMatchesSection(service, routeSection) &&
                matchesSearch(service)
        )
    }

    const getDetailRouteState = (service: TNormalizedService) => ({
        from: `${location.pathname}${location.search}`,
        service,
    })

    const navigateToServiceDetail = (service: TNormalizedService) => {
        navigate(service.detailPath, {
            state: getDetailRouteState(service),
        })
    }

    const shouldIgnoreRowClick = (target: EventTarget | null): boolean =>
        target instanceof HTMLElement &&
        Boolean(
            target.closest(
                'a, button, input, select, textarea, [role="button"]'
            )
        )

    const columns = [
        {
            Header: 'Product Code',
            accessor: 'productCode',
            minWidth: 180,
        },
        {
            Header: 'Name',
            accessor: 'name',
            minWidth: 240,
            Cell: ({ value }) => (
                <div
                    className="py-1 font-weight-bold"
                    style={{ wordBreak: 'break-word' }}
                >
                    {value}
                </div>
            ),
        },
        {
            Header: 'Service Group',
            accessor: 'groupName',
            Cell: ({ value }) =>
                value ? <MetaButton className="mr-2">{value}</MetaButton> : '',
            minWidth: 180,
        },
        {
            Header: 'Order Number',
            accessor: 'orderNumber',
            Cell: ({ value }) => value || '',
            minWidth: 180,
        },
        {
            Header: 'Active Since',
            accessor: 'goLiveDate',
            Cell: ({ value }) =>
                value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 140,
        },
        {
            Header: '',
            id: 'actions',
            accessor: 'detailPath',
            filterable: false,
            sortable: false,
            minWidth: 60,
            width: 60,
            maxWidth: 60,
            Cell: ({ original, value }) => (
                <Link
                    className="grey-icon rounded"
                    to={value}
                    state={getDetailRouteState(original)}
                    aria-label={`View details for ${original.name}`}
                    title={`View details for ${original.name}`}
                >
                    <SearchIcon title="details" />
                </Link>
            ),
        },
    ]

    return (
        <div className="animated fadeIn mb-3">
            <Section title="Services">
                <p className="text-muted mb-3">
                    Browse your services in a shared basic view. Extended
                    details are available when the service has extra
                    information.
                </p>
                <ServiceSubmenu
                    sections={navigationSections}
                    activeSectionId={activeSectionId}
                    className="justify-content-start flex-wrap"
                />
                <Row className="mb-4">
                    <Col md="8" className="d-flex flex-row mb-3 mb-md-0">
                        <SearchInput
                            value={searchString}
                            onChange={onChangeSearch}
                            clearable
                            onClear={onClearSearch}
                        />
                    </Col>
                    <Col
                        md="4"
                        className="d-flex justify-content-end align-items-start"
                    >
                        <Button
                            className="pt-1 px-1"
                            color="primary"
                            onClick={() => {
                                void getData()
                            }}
                        >
                            <Refresh
                                title="Refresh"
                                cssClassTop="refresh-svg"
                            />
                        </Button>
                    </Col>
                </Row>
                <Row className="mt-2 mr-0 ml-0">
                    <Col className="p-4 bg-white rounded">
                        <SimpleLoader loading={loading}>
                            <ComplexTable
                                columns={columns}
                                defaultSorted={{ id: 'goLiveDate', desc: true }}
                                data={getVisibleServices()}
                                tableClass="services-table"
                                getRowProps={(_finalState, rowInfo) => {
                                    if (!rowInfo?.original) {
                                        return {}
                                    }

                                    const service =
                                        rowInfo.original as TNormalizedService

                                    return {
                                        className:
                                            'service-table-row-clickable',
                                        onClick: (
                                            event: React.MouseEvent<HTMLElement>
                                        ) => {
                                            if (
                                                shouldIgnoreRowClick(
                                                    event.target
                                                )
                                            ) {
                                                return
                                            }

                                            navigateToServiceDetail(service)
                                        },
                                        onKeyDown: (
                                            event: React.KeyboardEvent<HTMLElement>
                                        ) => {
                                            if (
                                                event.key !== 'Enter' &&
                                                event.key !== ' '
                                            ) {
                                                return
                                            }

                                            event.preventDefault()
                                            navigateToServiceDetail(service)
                                        },
                                        tabIndex: 0,
                                        role: 'link',
                                        'aria-label': `View details for ${service.name}`,
                                    }
                                }}
                            />
                        </SimpleLoader>
                    </Col>
                </Row>
            </Section>
        </div>
    )
}

export default Services
